import { Box, type SxProps, type Theme } from '@mui/material';
import { NodeToolbar, Position, useNodeId, useStore } from '@xyflow/react';
import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLatest } from 'react-use';

import { type InlineTextEditorProps } from '@/components/diagrams/components/InlineTextEditor/InlineTextEditor';
import { TextNoteToolbarPlugin } from '@/components/diagrams/FlowChart/nodes/TextNoteNode/TextNoteToolbarPlugin';
import {
  TextEditor,
  type TextEditorProps,
} from '@/components/TextEditor/TextEditor';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import { SELECTION_TOOLBAR_OFFSET } from './DiagramLineToolbar';

/**
 * React Flow ignores drag, pan, and wheel gestures that start in elements
 * with these classes, so the user can select text in the editor.
 */
const REACT_FLOW_IGNORE_CLASSES = 'nodrag nopan nowheel';

/**
 * Makes the shared editor use the typography of the node. The editor styles
 * its blocks for documents: a white background, padding, and the `body-s`
 * font and gray color on each paragraph and list item. Notes must instead
 * take the font size, color, and alignment of the tile, such as white text
 * on a dark tile, so these rules make the blocks inherit them. Headings,
 * lists, and text formats keep their editor styles, except for color.
 */
const NOTES_INNER_SX: SxProps<Theme> = {
  background: 'transparent',
  // The editor clips its content to rounded corners. The notes have no
  // padding, so the corners would cut off the first and last letters.
  borderRadius: 0,
  // The editable element sets its font size and padding inline.
  '& [contenteditable]': {
    padding: '0 !important',
    minHeight: '0 !important',
    fontSize: 'inherit !important',
  },
  '& p, & li': {
    font: 'inherit',
    color: 'inherit',
    margin: 0,
    paddingBottom: 0,
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': { color: 'inherit' },
  '& ul, & ol': { paddingInlineStart: '1.25em' },
};

/** Lets the notes inherit the text styles of the node. */
function NotesWrapper({ children }: { children: ReactNode }) {
  return <Box width="100%">{children}</Box>;
}

/**
 * Shows the Markdown notes of a node with their formatting. Lexical reads
 * its initial value only once, so a new value mounts a new editor.
 */
export function DiagramNotesMarkdown({ markdown }: { markdown: string }) {
  return (
    <TextEditor
      key={markdown}
      initialValue={markdown}
      readOnly
      TextEditorWrapper={NotesWrapper}
      innerSx={NOTES_INNER_SX}
    />
  );
}

/**
 * The props of `InlineTextEditor`, so both editors of the node fields have
 * the same contract, and `onSave`.
 */
export type DiagramNotesEditorProps = Pick<
  InlineTextEditorProps,
  'label' | 'value' | 'onCommit' | 'onCancel' | 'onTab'
> & {
  /**
   * Saves the edited Markdown and keeps the editor open. The editor calls it
   * when the focus leaves the editor, for example to a toolbar menu or to a
   * button outside the canvas.
   */
  onSave: (markdown: string) => void;
};

/**
 * A rich-text editor for the Markdown notes of the React Flow node that
 * contains it. It shows a text toolbar above the node in place of the
 * selection toolbar. Enter starts a new paragraph. Delete removes the node.
 *
 * The edit ends when the user deselects the node, presses Tab, or presses
 * Escape. A blur does not end the edit, because the menus of the toolbar,
 * such as the block style and the font size, take the focus. A blur only
 * saves the draft.
 */
export function DiagramNotesEditor({
  label,
  value,
  onCommit,
  onCancel,
  onTab,
  onSave,
}: DiagramNotesEditorProps) {
  const nodeID = useNodeId() ?? '';
  const isSelected = useStore(
    (state) => !!state.nodeLookup.get(nodeID)?.selected
  );
  const { openNotesEditor, closeNotesEditor, removeElements } =
    useDiagramEditing();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const draft = useRef(value);
  // Escape, Tab, and Delete end the edit before the blur that follows them.
  // The blur must not end the edit again.
  const isFinished = useRef(false);

  useEffect(() => {
    openNotesEditor(nodeID);
    return () => closeNotesEditor(nodeID);
  }, [closeNotesEditor, nodeID, openNotesEditor]);

  const finish = (save: boolean) => {
    if (isFinished.current) return;
    isFinished.current = true;
    if (save) onCommit(draft.current.trimEnd());
    else onCancel();
  };
  const latestFinish = useLatest(finish);

  useEffect(() => {
    if (!isSelected) latestFinish.current(true);
  }, [isSelected, latestFinish]);

  // Lexical mounts the toolbar again when the plugin identity changes, so
  // the plugin depends only on values that keep their identity.
  const ToolbarPlugin = useMemo<TextEditorProps['ToolbarPlugin']>(
    () =>
      function DiagramNotesToolbar() {
        return (
          <NodeToolbar
            isVisible
            position={Position.Top}
            offset={SELECTION_TOOLBAR_OFFSET}
          >
            <TextNoteToolbarPlugin
              confirmDelete={false}
              onDelete={() => {
                isFinished.current = true;
                removeElements(nodeID);
              }}
            />
          </NodeToolbar>
        );
      },
    [nodeID, removeElements]
  );

  // Capture Escape and Tab before Lexical, which indents on Tab. Keys in the
  // toolbar menus also reach this handler through the React tree, so Escape
  // in a menu closes only the menu.
  const handleKeyDownCapture = (event: KeyboardEvent) => {
    if (!wrapperRef.current?.contains(event.target as Node)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finish(false);
    } else if (event.key === 'Tab' && !event.shiftKey && onTab) {
      event.preventDefault();
      event.stopPropagation();
      finish(true);
      onTab();
    }
  };

  const handleBlur = (event: FocusEvent) => {
    if (isFinished.current) return;
    if (wrapperRef.current?.contains(event.relatedTarget)) return;
    onSave(draft.current.trimEnd());
  };

  return (
    <Box
      ref={wrapperRef}
      role="group"
      aria-label={label}
      className={REACT_FLOW_IGNORE_CLASSES}
      sx={{ cursor: 'text' }}
      onBlur={handleBlur}
      onKeyDownCapture={handleKeyDownCapture}
      // Keep Backspace, Delete, and canvas shortcuts inside the editor.
      onKeyDown={(event) => event.stopPropagation()}
    >
      <TextEditor
        initialValue={value}
        initialValueKind="markdown"
        onImmediateUpdate={(markdown) => {
          draft.current = markdown;
        }}
        TextEditorWrapper={NotesWrapper}
        innerSx={NOTES_INNER_SX}
        ToolbarPlugin={ToolbarPlugin}
        allowTableInsert={false}
      />
    </Box>
  );
}
